// TODO: migrate from legacy CI tooling: resolve ts-* and/or eslint-* directives below (including inline comments)
/* eslint-disable @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unused-vars */

import React, { type FC, useState, useMemo } from "react";
import { useAuth0 } from "../../../../auth/auth0";
import { useUserAuth } from "../../../../hooks/useUserAuth";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	Segment,
	Header,
	Divider,
	Button,
	Table,
	Checkbox,
	Icon,
	Modal,
	type DropdownItemProps,
	Dropdown,
} from "semantic-ui-react";
import Tooltip from "rc-tooltip";
import DeleteConfirmationModal from "../FieldWizard/deleteModal";
import * as factorApi from "../../../../apis/factorApi";
import * as fieldApi from "../../../../apis/fieldApi";
import { deleteDataLayerFieldAssociation } from "../../../../apis/dataLayerApi";
import type {
	ExperimentalDesignFactor,
	ExperimentalModalInfo,
	FactorPlot,
	FieldOutline,
} from "../../../../apis/types/factorTypes";
import { ApplicationArea } from "../../../Lumber/ApplicationAreas";
import _ from "lodash";
import { toast } from "react-toastify";
import "./style.css";
import ExperimentalDesignModal from "./ExperimentalDesignModal";
import { fetchTreatmentTypes } from "../../../../apis/treatmentApi";

type Props = {
	fieldId: string;
	fromViewer: boolean;
	navigateToNextStep: () => void;
	navigateBack: () => void;
	handleCancel: () => void;
	handleDeleteField: () => Promise<void>;
	isDeleting: boolean;
};

type DeleteModalInfo = {
	factorName: string | null;
	source: string | null;
	dataLayerId: string | null;
	relatedFactorNames: string[] | null;
	open: boolean;
};

const ExperimentalDesigns: FC<Props> = ({
	fieldId,
	fromViewer,
	navigateToNextStep,
	navigateBack,
	handleCancel,
	handleDeleteField,
	isDeleting,
}) => {
	const userAuth = useUserAuth();
	const { getTokenSilently } = useAuth0();
	const queryClient = useQueryClient();

	const [isSaving, setIsSaving] = useState(false);
	const [deleteModalInfo, setDeleteModal] = useState<DeleteModalInfo>({
		factorName: null,
		source: null,
		dataLayerId: null,
		relatedFactorNames: null,
		open: false,
	});
	const [experimentalModalInfo, setExperimentalModalInfo] = useState<ExperimentalModalInfo>({
		displayId: null,
		designId: null,
		factorName: null,
		typeId: undefined,
		plots: null,
		default: false,
		open: false,
	});
	const [stagedExperimentalDesigns, setStagedExperimentalDesigns] = useState<ExperimentalDesignFactor[]>([]);
	const [collapseTreatments, setCollapseTreatments] = useState<boolean>(false);
	const [hoveredTreatment, setHoveredTreatment] = useState<string | null>(null);
	const [focusedTreatment, setFocusedTreatment] = useState<string | null>(null);

	const canEdit: boolean = useMemo(
		() =>
			userAuth.hasApplicationArea(ApplicationArea.FieldExperimentalDesignsCRUD, userAuth.currentClientId) ||
			userAuth.hasApplicationArea(ApplicationArea.TrialExperimentalDesignsCRUD, userAuth.currentClientId),
		[userAuth],
	);

	const getExperimentalDesigns = async () => {
		const accessToken = await getTokenSilently();
		const json = factorApi.getExperimentalDesigns(fieldId, userAuth.currentClientId!, accessToken);
		return json;
	};

	const getExperimentalDesignTypes = async () => {
		const accessToken = await getTokenSilently();
		const json = factorApi.getExperimentalDesignTypes(accessToken);
		return json;
	};

	const { data: experimentalDesignResponse } = useQuery({
		queryKey: ["experimental designs", fieldId],
		queryFn: () => getExperimentalDesigns(),
		select: (data) => data.data,
		enabled: !!userAuth.currentClientId,
		staleTime: Infinity,
	});

	const { data: experimentalDesignTypeResponse } = useQuery({
		queryKey: ["experimental design types"],
		queryFn: () => getExperimentalDesignTypes(),
		select: (data) => data.data,
		enabled: !!userAuth.currentClientId,
		staleTime: Infinity,
	});

	const getFieldOutline = async () => {
		const accessToken = await getTokenSilently();
		const json = fieldApi.getFieldOutlineData(userAuth.currentClientId!, fieldId, accessToken);
		return json;
	};

	const { data: fieldOutlineResponse } = useQuery({
		queryKey: ["field outline", fieldId],
		queryFn: () => getFieldOutline(),
		select: (data) => data as FieldOutline[],
		enabled: !!userAuth.currentClientId,
		staleTime: Infinity,
	});

	const { data: treatmentTypes } = useQuery({
		queryKey: ["treatment types", userAuth.currentClientId],
		queryFn: async () => fetchTreatmentTypes(userAuth.currentClientId, await getTokenSilently()),
		select: (data: { id: string; name: string }[]) => data.map((d) => ({ key: d.id, value: d.id, text: d.name })),
		enabled: !!userAuth.currentClientId,
		staleTime: Infinity,
	});

	const experimentalDesigns = useMemo(
		() =>
			stagedExperimentalDesigns.length
				? stagedExperimentalDesigns
				: experimentalDesignResponse
					? _.map(experimentalDesignResponse, (ed) => {
							ed.factorTreatments = _.sortBy(ed.factorTreatments, (ft) => ft.displayId);
							return ed;
						})
					: [],
		[stagedExperimentalDesigns, experimentalDesignResponse],
	);

	const typeOptions = useMemo<DropdownItemProps[]>(
		() => _.map(experimentalDesignTypeResponse, (type) => ({ key: type.id, value: type.id, text: type.name })),
		[experimentalDesignTypeResponse],
	);

	const fieldOutline = useMemo<FieldOutline[]>(() => fieldOutlineResponse ?? [], [fieldOutlineResponse]);

	const saveFactors = (continueToNextStep: boolean, finish: boolean) => {
		setIsSaving(true);
		const expDesignClone = _.cloneDeep(experimentalDesigns);

		const deleteList = _.filter(expDesignClone, (ed) => ed.delete && ed.source === "Experimental Design");

		const unlinkList = _.uniqBy(
			_.filter(expDesignClone, (ed) => ed.delete && ed.source !== "Experimental Design"),
			"dataLayerId",
		) as ExperimentalDesignFactor[];

		//-- Edge case when no default is selected
		if (
			!_.some(
				_.filter(expDesignClone, (ed) => !ed.delete),
				(ed: ExperimentalDesignFactor) => ed.default,
			)
		) {
			expDesignClone[0].default = true;
			expDesignClone[0].isDirty = true;
		}

		const editList = _.filter(expDesignClone, (ed) => ed.isDirty && !ed.delete) as ExperimentalDesignFactor[];

		if (deleteList.length > 0) {
			deleteExperimentalDesigns(_.map(deleteList, "id"));
		}

		if (unlinkList.length > 0) {
			_.forEach(unlinkList, async (factor) => {
				await deleteDataLayerFieldAssociationMutationAsync({
					getAccessToken: getTokenSilently,
					clientId: userAuth.currentClientId!,
					dataLayerId: factor.dataLayerId!,
					fieldId: fieldId,
				});
			});
		}

		if (editList.length > 0) {
			editExperimentalDesignInfo(editList);
		}

		if (continueToNextStep) {
			navigateToNextStep();
		}

		setIsSaving(false);
	};

	const editExperimentalDesignInfoFn = async (expDesigns: ExperimentalDesignFactor[]) => {
		setIsSaving(true);
		const accessToken = await getTokenSilently();
		return factorApi.editExperimentalDesignInfo(expDesigns, userAuth.currentClientId!, accessToken);
	};

	const { mutate: editExperimentalDesignInfo } = useMutation({
		mutationFn: (expDesigns: ExperimentalDesignFactor[]) => editExperimentalDesignInfoFn(expDesigns),
		onSuccess: async (data) => {
			if (data.statusCode !== 200) {
				toast.error("Failed to update factor info");
			} else {
				toast.success("Factor info updated successfully");
				const expDesignClone = _.cloneDeep(experimentalDesigns);
				_.map(expDesignClone, (exp) => (exp.isDirty = false));

				setStagedExperimentalDesigns(expDesignClone);
				await queryClient.invalidateQueries({ queryKey: ["experimental designs", fieldId] });
			}

			setIsSaving(false);
		},
	});

	const deleteExperimentalDesignsFn = async (designIds: string[]) => {
		const accessToken = await getTokenSilently();
		return factorApi.deleteExperimentalDesigns(designIds, accessToken);
	};

	const { mutate: deleteExperimentalDesigns } = useMutation({
		mutationFn: (designIds: string[]) => deleteExperimentalDesignsFn(designIds),
		onSuccess: async (data) => {
			if (data.statusCode !== 200) {
				toast.error("Failed to delete experimental designs");
				console.error(data.messages[0]);
			} else {
				toast.success("Experimental Designs deleted successfully");
				const experimentalDesignsClone = _.filter(experimentalDesigns, (ed) => !ed.delete);
				setStagedExperimentalDesigns(experimentalDesignsClone);
				await queryClient.invalidateQueries({ queryKey: ["experimental designs", fieldId] });
			}
		},
	});

	const handleVisualDelete = () => {
		//-- Search based on name since id may not exist
		let expDesignIndex = _.findIndex(experimentalDesigns, (ed) => ed.name === deleteModalInfo.factorName);
		const editExpDesigns = _.cloneDeep(experimentalDesigns);

		if (expDesignIndex !== -1) {
			if (experimentalDesigns[expDesignIndex].id === null) {
				editExpDesigns.splice(expDesignIndex, 1);
			} else {
				editExpDesigns[expDesignIndex] = { ...editExpDesigns[expDesignIndex], delete: true, isDirty: true };
			}
		}

		//-- Workflow for unlinking related factors in same data layer
		if (deleteModalInfo.source !== "Experimental Design") {
			_.map(
				_.filter(experimentalDesigns, (ed) => ed.dataLayerId === deleteModalInfo.dataLayerId),
				(ed) => {
					expDesignIndex = _.indexOf(experimentalDesigns, ed);

					editExpDesigns[expDesignIndex] = { ...editExpDesigns[expDesignIndex], delete: true, isDirty: true };
				},
			);
		}

		setStagedExperimentalDesigns(editExpDesigns);

		setDeleteModal((deleteModalInfo) => ({ ...deleteModalInfo, open: false }));
	};

	const { mutateAsync: deleteDataLayerFieldAssociationMutationAsync } = useMutation({
		mutationFn: deleteDataLayerFieldAssociation,
		onSuccess: async () => {
			toast.success("Data Layers unlinked successfully", { toastId: "unlink" });
			await queryClient.invalidateQueries({ queryKey: ["experimental designs", fieldId] });
		},
	});

	return (
		<>
			<Segment basic style={{ padding: 0 }}>
				<Button
					id="form-button-back"
					color="black"
					compact
					content={"Back"}
					disabled={isSaving}
					onClick={() => {
						navigateBack();
					}}
				/>
				<Button
					id="form-button-save-and-continue"
					floated="right"
					primary
					compact
					content={"Save & Continue"}
					onClick={() => {
						saveFactors(true, false);
					}}
					disabled={isSaving || !_.some(experimentalDesigns, (ed) => ed.isDirty)}
					loading={isSaving}
				/>
				<Button
					id="form-button-save"
					floated="right"
					color="blue"
					compact
					content={"Save"}
					onClick={() => {
						saveFactors(false, false);
					}}
					disabled={isSaving || !_.some(experimentalDesigns, (ed) => ed.isDirty)}
					loading={isSaving}
				/>
				{!fromViewer && (
					<DeleteConfirmationModal
						handleDeleteField={handleDeleteField}
						floated="right"
						isDeleting={isDeleting}
						disabled={isSaving || isDeleting}
						loading={isDeleting}
					/>
				)}
				{fromViewer && (
					<Button
						id="form-button-cancel"
						type="button"
						floated="right"
						compact
						disabled={isSaving}
						onClick={() => {
							handleCancel();
						}}
					>
						Cancel
					</Button>
				)}
				<div style={{ display: "flex", flexDirection: "row" }}>
					<Header as="h2" style={{ marginTop: "1rem", marginBottom: 0 }}>
						Experimental Design Factors
					</Header>
					<div style={{ display: "flex", position: "absolute", right: 0, marginTop: "1rem" }}>
						<Button
							compact
							content={collapseTreatments ? "Show Treatment Info" : "Hide Treatment Info"}
							onClick={() => setCollapseTreatments(!collapseTreatments)}
						/>
					</div>
				</div>

				<Table celled striped selectable color="blue" textAlign="center">
					<Table.Header>
						<Table.Row>
							<Table.HeaderCell>Design ID</Table.HeaderCell>
							<Table.HeaderCell>Design Name</Table.HeaderCell>
							<Table.HeaderCell>Type</Table.HeaderCell>
							<Table.HeaderCell>Source</Table.HeaderCell>
							<Table.HeaderCell>Treatment</Table.HeaderCell>
							<Table.HeaderCell>Treatment Name</Table.HeaderCell>
							<Table.HeaderCell>Treatment Type</Table.HeaderCell>
							<Table.HeaderCell>Default</Table.HeaderCell>
							<Table.HeaderCell>Actions</Table.HeaderCell>
						</Table.Row>
					</Table.Header>
					<Table.Body>
						{_.map(
							_.filter(experimentalDesigns, (ed) => !ed.delete && ed.source === "Experimental Design"),
							(ed) => (
								<Table.Row key={ed.id}>
									<Table.Cell>{`EXP${_.indexOf(
										_.filter(experimentalDesigns, (filter) => filter.source === ed.source && !filter.delete),
										ed,
									)}`}</Table.Cell>
									<Table.Cell>{ed.name}</Table.Cell>
									<Table.Cell>
										{ed.factorTypeName ?? _.find(typeOptions, (t) => t.key === ed.factorTypeId)?.text}
									</Table.Cell>
									<Table.Cell>{ed.source}</Table.Cell>
									<Table.Cell>
										{collapseTreatments
											? ed.factorTreatments.length === 1
												? ed.factorTreatments[0].displayId
												: `${_.minBy(ed.factorTreatments, (t) => t.displayId)?.displayId} - ${_.maxBy(ed.factorTreatments, (t) => t.displayId)?.displayId}`
											: _.map(ed.factorTreatments, (treatment, ii) => {
													if (ii !== 0) {
														return (
															<>
																{ii > 1 && <Divider />}
																{treatment.displayId}
															</>
														);
													} else if (ed.factorTreatments.length === 1) {
														return <>{treatment.displayId}</>;
													} else return <></>;
												})}
									</Table.Cell>
									<Table.Cell>
										{collapseTreatments
											? "..."
											: _.map(ed.factorTreatments, (treatment, ii) => {
													const hoverKey = `${ed.id}-${ii}`;
													if (ii !== 0) {
														return (
															<>
																{ii > 1 && <Divider />}
																{treatment.name === "unassigned" ? (
																	treatment.name
																) : (
																	<div
																		style={{
																			position: "relative",
																			display: "flex",
																			alignItems: "center",
																			backgroundColor: focusedTreatment === hoverKey ? "white" : undefined,
																			border:
																				focusedTreatment === hoverKey
																					? "1px solid var(--gray-300, #ccc)"
																					: "1px solid transparent",
																			borderRadius: 3,
																			padding: "2px 4px",
																		}}
																		onMouseEnter={() => setHoveredTreatment(hoverKey)}
																		onMouseLeave={() => setHoveredTreatment(null)}
																	>
																		{hoveredTreatment === hoverKey && focusedTreatment !== hoverKey && (
																			<Icon
																				name="pencil"
																				size="large"
																				style={{
																					position: "absolute",
																					color: "var(--gray-500, #aaa)",
																					cursor: "default",
																				}}
																			/>
																		)}
																		<input
																			value={treatment.name}
																			type="text"
																			style={{ all: "unset", width: "100%" }}
																			onFocus={() => setFocusedTreatment(hoverKey)}
																			onBlur={() => setFocusedTreatment(null)}
																			onChange={({ target }) => {
																				const expDesignClone = _.cloneDeep(experimentalDesigns);
																				const matchingIndex = _.findIndex(
																					experimentalDesigns,
																					(exp) => exp.id === ed.id,
																				);

																				expDesignClone[matchingIndex].isDirty = true;
																				expDesignClone[matchingIndex].factorTreatments[ii].name = target.value;

																				setStagedExperimentalDesigns(expDesignClone);
																			}}
																		/>
																	</div>
																)}
															</>
														);
													} else if (ed.factorTreatments.length === 1) {
														return <>{treatment.name}</>;
													} else return <></>;
												})}
									</Table.Cell>
									<Table.Cell>
										{collapseTreatments
											? "..."
											: !!treatmentTypes &&
												_.map(ed.factorTreatments, (treatment, ii) => {
													return (
														ii > 0 && (
															<>
																<Dropdown
																	value={treatment.treatmentTypeId}
																	// defaultValue={ii === 1 ? treatmentTypes[0].key : treatmentTypes[1].key}
																	options={treatmentTypes ?? []}
																	onChange={(_e, { value }) => {
																		const expDesignClone = _.cloneDeep(experimentalDesigns);
																		const matchingIndex = _.findIndex(experimentalDesigns, (exp) => exp.id === ed.id);

																		expDesignClone[matchingIndex].isDirty = true;
																		expDesignClone[matchingIndex].factorTreatments[ii].treatmentTypeId =
																			value as string;

																		setStagedExperimentalDesigns(expDesignClone);
																	}}
																/>
																{ii < ed.factorTreatments.length - 1 && <Divider />}
															</>
														)
													);
												})}
									</Table.Cell>
									<Table.Cell>
										<Checkbox
											checked={ed.default}
											onClick={(e, { checked }) => {
												const clone = _.cloneDeep(experimentalDesigns);
												const currentDefaultIndex = _.indexOf(
													clone,
													_.find(clone, (c) => c.default),
												);

												const matchingIndex = _.findIndex(experimentalDesigns, (exp) => exp.id === ed.id);

												if (currentDefaultIndex !== -1 && matchingIndex !== currentDefaultIndex) {
													clone[currentDefaultIndex].default = false;
													clone[currentDefaultIndex].isDirty = true;
												}

												clone[matchingIndex].default = checked as boolean;
												clone[matchingIndex].isDirty = true;

												setStagedExperimentalDesigns(clone);
											}}
										/>
									</Table.Cell>
									<Table.Cell>
										{ed.name !== "Whole Field" && (
											<>
												<Tooltip
													placement="bottom"
													mouseEnterDelay={0}
													mouseLeaveDelay={0}
													trigger="hover"
													overlay={"Edit"}
													motion={{ motionName: "rc-tooltip-zoom" }}
												>
													<a
														style={{ cursor: "pointer" }}
														onClick={() => {
															setExperimentalModalInfo({
																designId: ed.id,
																displayId: `EXP${_.indexOf(
																	_.filter(
																		experimentalDesigns,
																		(filter) => filter.source === ed.source && !filter.delete,
																	),
																	ed,
																)}`,
																factorName: ed.name,
																typeId: ed.factorTypeId,
																plots: ed.factorPlots,
																default: ed.default,
																open: true,
															});
														}}
													>
														<Icon className="pencil" />
													</a>
												</Tooltip>
												{canEdit && (
													<Tooltip
														placement="bottom"
														mouseEnterDelay={0}
														mouseLeaveDelay={0}
														trigger="hover"
														overlay={"Delete"}
														motion={{ motionName: "rc-tooltip-zoom" }}
													>
														<a
															style={{ cursor: "pointer" }}
															onClick={() => {
																setDeleteModal({
																	factorName: ed.name,
																	source: ed.source,
																	dataLayerId: null,
																	relatedFactorNames: null,
																	open: true,
																});
															}}
														>
															<Icon className="trash" />
														</a>
													</Tooltip>
												)}
											</>
										)}
									</Table.Cell>
								</Table.Row>
							),
						)}
					</Table.Body>
				</Table>
				<Segment basic>
					<Button
						icon="add"
						labelPosition="left"
						disabled={!canEdit}
						content="Add Experimental Design Factor"
						onClick={() =>
							setExperimentalModalInfo(() => ({
								designId: null,
								displayId: `EXP${_.filter(experimentalDesigns, (filter) => filter.source === "Experimental Design" && !filter.delete).length}`,
								factorName: null,
								typeId: _.find(typeOptions, (opt) => opt.text === "Unstructured")?.key,
								plots: _.map(fieldOutline, (p) => ({
									plotId: p.plotId,
									plotGroupingDisplayId: null,
									treatmentDisplayId: null,
									treatmentName: null,
									replicate: null,
									seed: p.seed,
									recId: p.recId,
								})) as FactorPlot[],
								//-- This will set EXP1 to default if user is currently adding it
								default:
									_.filter(experimentalDesigns, (filter) => filter.source === "Experimental Design" && !filter.delete)
										.length === 1,
								open: true,
							}))
						}
					/>
				</Segment>
				<Header as="h2">Associated Factors</Header>
				<Table celled striped selectable color="blue" textAlign="center">
					<Table.Header>
						<Table.Row>
							<Table.HeaderCell>Factor ID</Table.HeaderCell>
							<Table.HeaderCell>Factor Name</Table.HeaderCell>
							<Table.HeaderCell>Source</Table.HeaderCell>
							<Table.HeaderCell>Treatment</Table.HeaderCell>
							<Table.HeaderCell>Treatment Name</Table.HeaderCell>
							<Table.HeaderCell>Actions</Table.HeaderCell>
						</Table.Row>
					</Table.Header>
					<Table.Body>
						{_.map(
							_.filter(experimentalDesigns, (ed) => !ed.delete && ed.source !== "Experimental Design"),
							(ed) => (
								<Table.Row key={ed.dataLayerUploadId}>
									<Table.Cell>{`VEC${
										_.indexOf(
											_.filter(experimentalDesigns, (filter) => filter.source === ed.source && !filter.delete),
											ed,
										) + 1
									}`}</Table.Cell>
									<Table.Cell>{ed.name}</Table.Cell>
									<Table.Cell>{ed.source}</Table.Cell>
									<Table.Cell>
										{collapseTreatments
											? ed.factorTreatments.length === 1
												? ed.factorTreatments[0].displayId
												: `${_.minBy(ed.factorTreatments, (t) => t.displayId)?.displayId} - ${_.maxBy(ed.factorTreatments, (t) => t.displayId)?.displayId}`
											: _.map(ed.factorTreatments, (treatment, ii) => {
													if (ii !== 0) {
														return (
															<>
																{ii > 1 && <Divider />}
																{treatment.displayId}
															</>
														);
													} else if (ed.factorTreatments.length === 1) {
														return <>{treatment.displayId}</>;
													} else return <></>;
												})}
									</Table.Cell>
									<Table.Cell>
										{collapseTreatments
											? "..."
											: _.map(ed.factorTreatments, (treatment, ii) => {
													if (ii !== 0) {
														return (
															<>
																{ii > 1 && <Divider />}
																{treatment.displayId}
															</>
														);
													} else if (ed.factorTreatments.length === 1) {
														return <>{treatment.name}</>;
													} else return <></>;
												})}
									</Table.Cell>
									<Table.Cell>
										<Tooltip
											placement="bottom"
											mouseEnterDelay={0}
											mouseLeaveDelay={0}
											trigger="hover"
											overlay={"Delete"}
											motion={{ motionName: "rc-tooltip-zoom" }}
										>
											<a
												style={{ cursor: "pointer" }}
												onClick={() => {
													setDeleteModal({
														factorName: ed.name,
														source: ed.source,
														dataLayerId: ed.dataLayerId,
														relatedFactorNames: _.map(
															_.filter(
																experimentalDesigns,
																(exp) => exp.dataLayerId === ed.dataLayerId && exp.name !== ed.name,
															),
															"name",
														),
														open: true,
													});
												}}
											>
												<Icon className="trash" />
											</a>
										</Tooltip>
									</Table.Cell>
								</Table.Row>
							),
						)}
					</Table.Body>
				</Table>
			</Segment>
			<Modal open={deleteModalInfo.open}>
				<Modal.Header>
					Delete {deleteModalInfo.source} {deleteModalInfo.source !== "Experimental Design" ? "Link" : ""}
				</Modal.Header>
				<Modal.Content>
					<Modal.Description>
						<p>Are you sure you want to delete {deleteModalInfo.factorName}?</p>
						<p>You will still need to click Save to apply changes</p>
						{deleteModalInfo.source !== "Experimental Design" &&
							deleteModalInfo.relatedFactorNames &&
							deleteModalInfo.relatedFactorNames?.length > 0 && (
								<>
									<p>Unlinking this factor will also unlink:</p>
									<ul>
										{_.map(deleteModalInfo.relatedFactorNames, (name) => (
											<li>{name}</li>
										))}
									</ul>
								</>
							)}
					</Modal.Description>
				</Modal.Content>
				<Modal.Actions>
					<Button floated="right" negative onClick={() => handleVisualDelete()}>
						Delete
					</Button>
					<Button
						floated="right"
						onClick={() =>
							setDeleteModal(() => {
								return { ...deleteModalInfo, open: false };
							})
						}
					>
						Cancel
					</Button>
					<br style={{ clear: "both" }} />
				</Modal.Actions>
			</Modal>
			<ExperimentalDesignModal
				typeOptions={typeOptions}
				experimentalModalInfo={experimentalModalInfo}
				setExperimentalModalInfo={setExperimentalModalInfo}
				experimentalDesigns={experimentalDesigns}
				setExperimentalDesigns={setStagedExperimentalDesigns}
				fieldOutline={fieldOutline}
				fieldId={fieldId}
				canEdit={canEdit}
			/>
		</>
	);
};

export default ExperimentalDesigns;
